'use client';

import { Box, CircularProgress } from '@mui/material';
import { Typography } from '@/primitives';
import { useAnimalPedigree } from '@/hooks/publicSearch/animal/useAnimalPedigree';
import { useGetPedigreeByEntityGuid } from '@/hooks/entityApi/useGetPedigreeByEntityGuid';
import { useRegistryRoutes } from '@/hooks/useRegistryRoutes';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { PedigreeNodeDto } from '@/apiClient';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { GetAnimalPedigreeQuery } from '@/publicSearch/graphql/queries.generated';
import { MAX_PEDIGREE_DEPTH, PEDIGREE_FETCH_GENERATIONS } from './constants';
import PedigreeTree, { PedigreeEntry } from './PedigreeTree';
import { adaptPedigreeNode } from './pedigreeAdapter';

// Public data adapter
type PedigreeNode = GetAnimalPedigreeQuery['pedigree']['nodes'][0];
type PedigreeRoot = NonNullable<GetAnimalPedigreeQuery['pedigree']['root']>;

/** Rebuilds the public GraphQL flat-node list into a recursive sire/dam tree. */
function buildSimpleTree(
  pedigree: GetAnimalPedigreeQuery['pedigree'],
): PedigreeEntry | null {
  if (!pedigree.root) return null;

  const childrenOf = new Map<
    number,
    { sire?: PedigreeNode; dam?: PedigreeNode }
  >();
  for (const node of pedigree.nodes) {
    if (node.parentId == null) continue;
    const entry = childrenOf.get(node.parentId) ?? {};
    if (node.parentSide === 'S') entry.sire = node;
    else if (node.parentSide === 'D') entry.dam = node;
    childrenOf.set(node.parentId, entry);
  }

  // `ancestors` tracks the current root→node path so a self-referential parent
  // (animal listed as its own ancestor) terminates instead of looping forever.
  // Linebreeding (same animal in multiple positions on different paths) is
  // preserved because each branch gets its own copy of the set.
  function build(
    animalId: number,
    node: PedigreeNode | PedigreeRoot,
    depth: number,
    ancestors: Set<number>,
  ): PedigreeEntry {
    const children = childrenOf.get(animalId);
    const entry: PedigreeEntry = {
      id: animalId,
      name: node.name ?? '',
      ident: node.ident ?? null,
      sex: node.sex ?? null,
      dob: node.dob ?? null,
      status: node.status ?? null,
    };
    if (depth >= MAX_PEDIGREE_DEPTH - 1) return entry;
    const nextAncestors = new Set(ancestors).add(animalId);
    if (children?.sire && !nextAncestors.has(children.sire.animalId)) {
      entry.sire = build(
        children.sire.animalId,
        children.sire,
        depth + 1,
        nextAncestors,
      );
    }
    if (children?.dam && !nextAncestors.has(children.dam.animalId)) {
      entry.dam = build(
        children.dam.animalId,
        children.dam,
        depth + 1,
        nextAncestors,
      );
    }
    return entry;
  }

  return build(pedigree.root.animalId, pedigree.root, 0, new Set());
}

/**
 * Returns `undefined` when the node has no usable identity — see pedigreeAdapter.
 */
function adaptNode(
  node: PedigreeNodeDto,
  depth: number = 0,
): PedigreeEntry | undefined {
  return adaptPedigreeNode(node, depth, MAX_PEDIGREE_DEPTH);
}

export const PEDIGREE_TYPE = {
  PUBLIC: 'public',
  REGISTRY: 'registry',
} as const;

/** Loading / error / empty chrome around `PedigreeTree`. */
function PedigreeDisplay({
  root,
  loading,
  error,
  nodeHrefPrefix,
  linkRootAnimal,
}: {
  root: PedigreeEntry | null | undefined;
  loading: boolean;
  error?: unknown;
  nodeHrefPrefix: string;
  linkRootAnimal?: boolean;
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography
          variant="bodysmall"
          sx={{
            color: 'text.secondary',
          }}
        >
          Loading pedigree…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography
        variant="bodysmall"
        sx={{
          color: 'text.secondary',
        }}
      >
        Unable to load pedigree.
      </Typography>
    );
  }

  if (!root) {
    return (
      <Typography
        variant="bodysmall"
        sx={{
          color: 'text.secondary',
        }}
      >
        No pedigree available.
      </Typography>
    );
  }

  return (
    <PedigreeTree
      root={root}
      nodeHrefPrefix={nodeHrefPrefix}
      linkRootAnimal={linkRootAnimal}
    />
  );
}

function PublicPedigreeContent({
  animalId,
  orgId,
  linkRootAnimal,
}: {
  animalId: string;
  orgId?: string | number;
  linkRootAnimal?: boolean;
}) {
  const { loading, error, raw } = useAnimalPedigree(animalId, orgId, 5);
  const societyCode = usePublicSearchContext(
    (context) => context.society.societyCode ?? '',
  );
  const nodeHrefPrefix = societyCode
    ? `/${societyCode}/${ROUTE_SEGMENT.ANIMALS}`
    : '';
  const root = raw?.pedigree ? buildSimpleTree(raw.pedigree) : null;
  return (
    <PedigreeDisplay
      root={root}
      loading={loading}
      error={error}
      nodeHrefPrefix={nodeHrefPrefix}
      linkRootAnimal={linkRootAnimal}
    />
  );
}

function RegistryPedigreeContent({ entityGuidId }: { entityGuidId?: string }) {
  const { data, isLoading, error } = useGetPedigreeByEntityGuid(
    entityGuidId ?? '',
    PEDIGREE_FETCH_GENERATIONS,
  );
  const registryRoutes = useRegistryRoutes();
  const root = data?.data ? adaptNode(data.data) : undefined;
  return (
    <PedigreeDisplay
      root={root}
      loading={isLoading}
      error={error}
      nodeHrefPrefix={registryRoutes.ANIMALS}
    />
  );
}

type PublicProps = {
  type: typeof PEDIGREE_TYPE.PUBLIC;
  animalId: string;
  orgId?: string | number;
  linkRootAnimal?: boolean;
};

type RegistryProps = {
  type: typeof PEDIGREE_TYPE.REGISTRY;
  entityGuidId?: string;
};

type Props = PublicProps | RegistryProps;

export default function PedigreeTab(props: Props) {
  if (props.type === PEDIGREE_TYPE.PUBLIC) {
    return (
      <PublicPedigreeContent
        animalId={props.animalId}
        orgId={props.orgId}
        linkRootAnimal={props.linkRootAnimal}
      />
    );
  }
  return <RegistryPedigreeContent entityGuidId={props.entityGuidId} />;
}
